import isObject from 'lodash/isObject'

import { getPathLocale } from '@src/i18n/locale.util'
import { getIsServer } from '@src/utils/common.util'
import { JSendError, JSendFailure, type JSendResponse, isJSend } from '@src/utils/jsend.util'
import { isSensitiveKey, log } from '@src/utils/log.util'
import { clone } from '@src/utils/object.util'
import * as serverActions from '@src/utils/server-actions.util'

type ActionFn = (...args: any) => Promise<any>

type Actions = typeof serverActions

// Removes JSendResponse type from actions return type, as we capture and throw them.
export type ActionsThrowJSendError = {
  [Action in keyof Actions]: (
    ...args: Parameters<Actions[Action]>
  ) => Promise<Exclude<Awaited<ReturnType<Actions[Action]>>, JSendResponse>>
}

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/** Used to test an action with a mock request
 * @example
 * actionMockRequests = { 'createOrder': { ... } }
 * actionMockRequests = { 'addLineItems': [{ sku: 'TEST', quantity: 1 }] }
 */
function getActionsMockRequest(severAction: string) {
  const actionMockRequests = globalThis.actionMockRequests || {}
  return actionMockRequests[severAction] || null
}

/** Used to test an action with a mock response
 * @example
 * actionMockResponses = { 'createOrder': { status: 'success', data: { ... } } }
 * actionMockResponses = { 'createOrder': { status: 'fail', data: { code: 'invalid-items' } } }
 */
function getActionMockResponse(serverAction: string) {
  const actionMockResponses = globalThis.actionMockResponses || {}
  return actionMockResponses[serverAction] || null
}
/* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

// Wrap server actions with logging
function withLogging(serverAction: string, fn: ActionFn) {
  return async function (...args: any[]) {
    const isBrowser = !getIsServer()
    const startTime = Date.now()

    const mockRequest = getActionsMockRequest(serverAction)
    if (mockRequest) args = [mockRequest]

    try {
      // Redact sensitive args before logging
      let redactedArgs = clone(args)
      if (serverAction === 'setCookie') {
        // Redact values in sensitively named cookies
        if (isSensitiveKey(args[0])) redactedArgs[1] = '[REDACTED]'
      } else if (['getCookie', 'removeCookie'].includes(serverAction)) {
        // Skip redaction on known safe server actions
        redactedArgs = redactedArgs
      } else if (isSensitiveKey(serverAction)) {
        // Redact non object arguments in sensitively named server actions
        redactedArgs = redactedArgs.map((arg) => (isObject(arg) ? arg : '[REDACTED]'))
      }

      log.info(`⏳ serverAction: ${serverAction}`, { args: redactedArgs })

      const mockResponse = getActionMockResponse(serverAction)
      const result = mockResponse ?? (await fn(...args))

      if (isJSend(result)) {
        if (result.status === 'fail') throw new JSendFailure(result)
        if (result.status === 'error') throw new JSendError(result)
      }

      // Redact sensitive results before logging
      let redactedResult = clone(result)
      if (serverAction === 'getCookie') {
        // Redact values in sensitively named cookies
        if (isSensitiveKey(redactedResult.name)) redactedResult.value = '[REDACTED]'
      } else if (isSensitiveKey(serverAction)) {
        // Redact string results in sensitively named server actions
        if (typeof redactedResult === 'string') redactedResult = '[REDACTED]'

        // Redact strings that are top-level members of array results in sensitively named server actions
        if (Array.isArray(redactedResult)) {
          redactedResult = redactedResult.map((item) =>
            typeof item === 'string' ? '[REDACTED]' : item
          )
        }
      }

      log.info(`✅ serverAction: ${serverAction}`, {
        result: isBrowser ? redactedResult : undefined,
        duration: `${Date.now() - startTime}ms`,
      })
      return result
    } catch (error: any) {
      log.error(`❌ ${serverAction} - ${error?.message}`, error)
      throw error
    }
  }
}

// Wrap server actions with a fetch to the server
function withApi(serverAction: string, fn: ActionFn) {
  return async function (...args: any[]) {
    let fnn = fn

    if (!getIsServer()) {
      fnn = async function (...args: any[]) {
        const locale = getPathLocale(window.location.pathname)
        const response = await fetch(`${locale}/action`, {
          method: 'POST',
          body: JSON.stringify({ action: serverAction, args }),
        })
        return await response.json()
      }
    }

    return await fnn(...args)
  }
}

/** Wrap server actions with analytics tracking. **/
function withAnalytics(serverAction: string, fn: ActionFn) {
  return async function (...args: any[]) {
    const result = await fn(...args)
    const isBrowser = !getIsServer()

    if (!isBrowser) return result

    // Track analytics for specific server actions called by 3rd-party integrations, add more as needed
    const { trackAddToCart } = globalThis.actionsAnalytics
    const actionName = serverAction as keyof Actions
    let actionAnalytic = Promise.resolve()

    if (actionName === 'addLineItems') {
      const [skus, redirectUrl] = args
      actionAnalytic = trackAddToCart(result as Commercetools.Cart, skus, redirectUrl)
    }

    if (actionName === 'upsellLineItems') {
      const [skus, redirectUrl] = args
      actionAnalytic = trackAddToCart(result as Commercetools.Cart, skus, redirectUrl)
    }

    await actionAnalytic.catch((error: any) => {
      log.error(`❌ [Action Analytics] ${serverAction} - ${error?.message}`, error)
    })

    return result
  }
}

export const ServerActions = {
  /**
   * Invoke server actions using Next.js built-in RPC strategy. This is the default behavior that should be used in most cases.
   * - concurrent actions are queued
   * - cookie updates revalidate the page
   **/
  serial: Object.entries(serverActions).reduce<ActionsThrowJSendError>(
    (acc, [serverAction, fn]) => {
      const serverActionFn = withLogging(serverAction, fn)

      return {
        ...acc,
        [serverAction]: serverActionFn,
      }
    },
    Object.create(null)
  ),
  /**
   * Invoke server actions through an API route (/us/en/action)
   * - concurrent actions are executed in parallel
   * - cookie updates don't revalidate the page
   **/
  parallel: Object.entries(serverActions).reduce<ActionsThrowJSendError>(
    (acc, [serverAction, fn]) => {
      let serverActionFn = withApi(serverAction, fn)
      serverActionFn = withLogging(serverAction, serverActionFn)

      return {
        ...acc,
        [serverAction]: serverActionFn,
      }
    },
    Object.create(null)
  ),
}

Object.assign(globalThis, {
  /**
   * Invoke server actions from the global context
   * - used for 3rd-party integrations (e.g. Digioh boxes)
   **/
  actions: Object.entries(serverActions).reduce<ActionsThrowJSendError>(
    (acc, [serverAction, fn]) => {
      let serverActionFn = withLogging(serverAction, fn)
      serverActionFn = withAnalytics(serverAction, serverActionFn)

      return {
        ...acc,
        [serverAction]: serverActionFn,
      }
    },
    Object.create(null)
  ),
  /**
   * Invoke server actions from the global context through an API route (e.g. /us/en/action)
   * - used for 3rd-party integrations (e.g. Digioh boxes)
   **/
  actionsAPI: Object.entries(serverActions).reduce<ActionsThrowJSendError>(
    (acc, [serverAction, fn]) => {
      let serverActionFn = withApi(serverAction, fn)
      serverActionFn = withLogging(serverAction, serverActionFn)
      serverActionFn = withAnalytics(serverAction, serverActionFn)

      return {
        ...acc,
        [serverAction]: serverActionFn,
      }
    },
    Object.create(null)
  ),
  actionsAnalytics: {},
})
